import { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { fetchStudentProfile, updateStudentAboutMe, updateStudentCountry, StudentProfile } from '@/services/studentProfileService';
import { getProfile, AccountDeactivatedError } from '@/services/UserManagmentServices';
import { useAuth } from '@/hooks/use-auth-compat';
import { RoleContext } from '@/contexts/RoleContext';
import { useQueryClient } from '@tanstack/react-query';

interface StudentProfileContextType {
  profile: StudentProfile | null;
  isLoading: boolean;
  error: Error | null;
  refreshProfile: (force?: boolean) => Promise<void>;
  updateAboutMe: (fullName: string) => Promise<void>;
  updateCountry: (countryCode: string) => Promise<void>;
}

const StudentProfileContext = createContext<StudentProfileContextType | undefined>(undefined);

export const StudentProfileProvider = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();
  const roleContext = useContext(RoleContext);
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState<StudentProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const profileRef = useRef<StudentProfile | null>(null);
  const currentTokenRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);
  // Holds the promise of the fetch that is currently in flight (null when idle).
  // A forced refresh that races an in-flight fetch awaits this instead of being
  // dropped, and instead of starting a second parallel fetch.
  const inFlightPromiseRef = useRef<Promise<void> | null>(null);

  const handleSetProfile = useCallback((p: StudentProfile | null) => {
    profileRef.current = p;
    setProfile(p);
  }, []);

  const refreshProfile = useCallback(async (force = false) => {
    const token = await auth.getToken();
    if (!token) return;

    // Skip if we already have the profile with the same token
    if (!force && profileRef.current && token === currentTokenRef.current) return;

    // A fetch is already in progress. Unforced callers are covered by it and
    // bail out silently (unchanged behaviour). Forced callers must NOT be
    // dropped — e.g. the coin balance is debited the moment an inquiry is sent,
    // and refreshProfile(true) is what surfaces that debit. Wait out the
    // in-flight fetch (so two fetches never race to setProfile) and then fall
    // through to a fresh one below: the in-flight request may have been issued
    // before the balance changed, so piggybacking on its result alone could
    // hand the caller a stale profile.
    if (isFetchingRef.current) {
      if (!force) return;
      while (isFetchingRef.current && inFlightPromiseRef.current) {
        await inFlightPromiseRef.current;
      }
    }

    isFetchingRef.current = true;
    currentTokenRef.current = token;
    setIsLoading(true);
    setError(null);
    const fetchPromise = (async () => {
      try {
        const data = await fetchStudentProfile(token);
        handleSetProfile(data);
      } catch (err) {
        console.error('Failed to refresh student profile:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
        inFlightPromiseRef.current = null;
      }
    })();
    inFlightPromiseRef.current = fetchPromise;
    await fetchPromise;
  }, [auth.getToken, handleSetProfile]);

  const updateAboutMe = useCallback(async (fullName: string) => {
    const token = await auth.getToken();
    if (!token) throw new Error('Not authenticated');
    const updated = await updateStudentAboutMe(token, { fullName });
    handleSetProfile(updated);
  }, [auth.getToken, handleSetProfile]);

  const updateCountry = useCallback(async (countryCode: string) => {
    const token = await auth.getToken();
    if (!token) throw new Error('Not authenticated');
    const updated = await updateStudentCountry(token, { countryCode });
    handleSetProfile(updated);
  }, [auth.getToken, handleSetProfile]);

  // Only fetch the student profile when the authenticated user is actually a student.
  // Teachers, unprovisioned users, and role-selection pages must not trigger
  // GET /api/v1/student/profile.
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await auth.getToken();
        if (!token || cancelled) return;
        const me = await queryClient.fetchQuery({
          queryKey: ['currentUser'],
          queryFn: () => getProfile(token),
          staleTime: 5 * 60 * 1000,
        });
        if (cancelled) return;
        if (me.defaultRole === 'STUDENT') {
          roleContext?.setRole('student');
        }
        // Load the student profile whenever the user actually holds a STUDENT
        // role — not only when it's their default. A multi-role user viewing the
        // student experience via role-switching (defaultRole TEACHER/ADMIN) still
        // has a student profile whose coin balance the coins page, dashboard, and
        // inquiry composer depend on. Gating on role membership (not defaultRole)
        // keeps the original guarantee that non-students never hit
        // GET /api/v1/student/profile and 404.
        if (me.roles?.some((r) => r.role === 'STUDENT')) {
          refreshProfile();
        }
      } catch (err) {
        if (err instanceof AccountDeactivatedError) {
          window.location.replace('/account-deactivated');
          return;
        }
        // 404 (unprovisioned) / 401 / network — swallow; OAuthCallback handles UX.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, auth.getToken, refreshProfile]);

  return (
    <StudentProfileContext.Provider value={{ profile, isLoading, error, refreshProfile, updateAboutMe, updateCountry }}>
      {children}
    </StudentProfileContext.Provider>
  );
};

export const useStudentProfile = () => {
  const context = useContext(StudentProfileContext);
  if (!context) {
    throw new Error('useStudentProfile must be used within StudentProfileProvider');
  }
  return context;
};
